import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import StyledTableRow from "app/Components/Tables/StyledTableRow";
import PrimaryTableCell from "app/Components/Tables/PrimaryTableCell";
import TableBody from "@mui/material/TableBody";

import { GameData, RootState, ModalDetails } from "types";
import GameInfoBtn from "app/Components/Buttons/FilledBtns/GameInfoBtn";
import SendReqBtn from "app/Components/Buttons/FilledBtns/SendReqBtn";
import calcDistance from "utils/calcDistance";
import sortGamesByStartTime from "utils/sortGamesByStartTime";
import { DayMap } from "utils/constants";

const tableCellStyles = {
  textAlign: "center" as const,
  margin: "auto",
  padding: "auto",
};

interface StatusTableCellProps {
  gameData: GameData;
}

const StatusTableCell = ({ gameData }: StatusTableCellProps) => {
  if (["Running, Delayed"].includes(gameData.status)) {
    return (
      <PrimaryTableCell sx={{ ...tableCellStyles, color: "red" }}>
        {gameData.status}
      </PrimaryTableCell>
    );
  }
  return (
    <PrimaryTableCell sx={tableCellStyles}>
      {`Starts ${DayMap[gameData.startDay as keyof typeof DayMap] || 'Unknown Day'} ${gameData.startTime}`}
    </PrimaryTableCell>
  );
};

interface TournamentTableBodyProps {
  data: Record<string, GameData> | null;
  setModalDets: (dets: ModalDetails) => void;
  setGameData: (data: GameData) => void;
}

const TournamentTableBody = ({ data, setModalDets, setGameData }: TournamentTableBodyProps) => {
  const navigate = useNavigate();

  const locationState = useSelector((state: RootState) => state.location);
  const { lat, lng, country } = locationState || {};

  if (!data) {
    return null;
  }

  const gameIds = sortGamesByStartTime(data);

  return (
    <TableBody>
      {gameIds.map((gameId) => {
        const gameData = data[gameId];

        return (
          <StyledTableRow key={gameId}>
            <PrimaryTableCell
              sx={{
                ...tableCellStyles,
                cursor: "pointer",
                maxWidth: window.innerWidth / 4,
                wordWrap: "break-word",
              }}
              onClick={() => {
                navigate(`/games/${gameId}`);
              }}
            >
              {gameData.gameName}
            </PrimaryTableCell>
            <PrimaryTableCell sx={tableCellStyles}>
              {gameData.game}
            </PrimaryTableCell>
            <PrimaryTableCell sx={tableCellStyles}>
              {gameData.buyin}
            </PrimaryTableCell>
            <PrimaryTableCell sx={tableCellStyles}>
              {gameData.fee}
            </PrimaryTableCell>
            <PrimaryTableCell sx={tableCellStyles}>
              {gameData.status}
            </PrimaryTableCell>

            <PrimaryTableCell sx={tableCellStyles}>
              {`${calcDistance(
                lat || 0,
                lng || 0,
                gameData.lat || 0,
                gameData.lng || 0,
                country || 'United States',
              )}mi`}
            </PrimaryTableCell>

            <StatusTableCell gameData={gameData} />
            <PrimaryTableCell sx={tableCellStyles}>
              <SendReqBtn
                action={() => setGameData(gameData)}
                gameId={gameId}
                cb={setModalDets}
              />
              <GameInfoBtn gameId={gameId} />
            </PrimaryTableCell>
          </StyledTableRow>
        );
      })}
    </TableBody>
  );
};

export default TournamentTableBody;
